import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { Auto, Switch } from './controls';
import { Activity, KnownWorkflow, workflows } from './workflow-api';
import { FindInPage, AssignmentSharp, FileCopy } from '@material-ui/icons/';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { withTooltip } from './format';
import { mediumGray, orange } from './theme';
import { organizationScope } from './organization-api';
import { showSuccessMessage } from './toast';

const width = { width: '100%' };

function FeatureForm({ form, label }) {
  const { enabled, create, update, delete: del, audit } = form.controls;

  return (
    <>
      <Divider orientation="horizontal" style={{ width: '100%' }} />
      <Grid item style={{ paddingTop: 6 }}>
        <Typography
          style={{ whiteSpace: 'nowrap' }}
          variant="body1"
          color="textSecondary"
        >
          {label}
        </Typography>
      </Grid>
      <Grid item container style={width}>
        <Grid item>
          <Switch {...enabled} />
        </Grid>
        <Grid item>
          <Switch {...create} />
        </Grid>
        <Grid item>
          <Switch {...update} />
        </Grid>
        <Grid item>
          <Switch {...del} />
        </Grid>
        <Grid item>
          <Switch {...audit} />
        </Grid>
      </Grid>
    </>
  );
}
type ActivityArgs = {
  activity: Activity;
};
function ActivityForm({ activity }: ActivityArgs) {
  const features = useRef(workflows.actions.featureForms(activity)).current;

  const [expanded, setExpanded] = useState(true);
  const onClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Grid item style={width}>
      <Accordion style={width} expanded={expanded}>
        <AccordionSummary
          onClick={onClick}
          style={{ height: 36, minHeight: 36, maxHeight: 36 }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography variant="overline" style={{ fontSize: 13 }}>
            {activity.name}
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ paddingTop: 0 }}>
          <Grid container direction="column" style={width}>
            {features.map(([f, form], i) => {
              return <FeatureForm key={i} form={form} label={f.feature} />;
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}

export function Workflows() {
  const { featureForm } = workflows.configuration;
  const list = workflows.data((s) => s.workflows);
  const workflow = workflows.data((s) => s.workflow);

  return list ? (
    <Grid
      container
      direction="column"
      alignItems="center"
      style={width}
      spacing={2}
    >
      {featureForm &&
        workflow &&
        workflow.activities &&
        workflow.activities.map((v, i) => (
          <ActivityForm key={i} activity={v} />
        ))}
    </Grid>
  ) : (
    <div>Loading...</div>
  );
}

let keyListener = false;
function addKeyListener() {
  if (!keyListener) {
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.altKey && e.code == 'KeyW' && workflows.data.getState().workflows) {
        workflows.actions.workflowSelectorOpen(true, true);
      }
    });
    keyListener = true;
  }
}

export function WorkflowsDialogue() {
  const open = workflows.data((s) => s.workflowSelectorOpen);
  const showActivities = workflows.data((s) => s.showActivities);

  const { forms } = workflows;

  const onClose = () => {
    workflows.actions.workflowSelectorOpen(false);
  };

  useEffect(() => {
    return addKeyListener();
  });

  const onCopy = (e: any) => {
    e.stopPropagation();
    window.navigator.clipboard.writeText(
      JSON.stringify(workflows.data.getState().workflow || {})
    );
    showSuccessMessage('Copied to Clipboard');
  };

  return (
    open && (
      <Dialog open={open} maxWidth={false}>
        <DialogTitle>
          <Grid
            container
            direction="row"
            alignItems="center"
            spacing={2}
            item
            style={{ width: '100%', flexWrap: 'nowrap' }}
          >
            <Grid item xs={11}>
              <Auto {...forms.workflow.controls.workflow} id={'workflow'} />
            </Grid>
            <Grid item xs={1}>
              {withTooltip(
                'Copy Workflow Configuration to Clipboard',
                <FileCopy onClick={onCopy} htmlColor={mediumGray} style={{}} />
              )}
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent
          style={{
            width: showActivities ? 622 : 300,
            height: showActivities ? 780 : undefined,
            marginLeft: 12,
            paddingBottom: 12,
          }}
        >
          {showActivities && <Workflows />}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="contained">
            {' '}
            Close{' '}
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
}

export type StatusProps = {
  label?: string;
  onClick?: (e?: any) => void;
  color?: string;
  workflow?: KnownWorkflow;
};

export function WorkflowStatus(props: StatusProps) {
  const department = organizationScope((s) => s.departmentCode);
  const disabled = department == null;

  const { workflow } = props;
  let w = workflows.data((s) => s.workflow);
  const flow: KnownWorkflow = workflow
    ? workflow
    : ((w?.name || '') as any);
  const { label, onClick, color } = props;

  const iconColor: any = color || orange;
  let icon: any;

  switch (flow) {

    case 'codeReviewStd':
    case 'auditStd': {
      icon = <FindInPage htmlColor={iconColor} />;
      break;
    }
    default :
    icon = <AssignmentSharp htmlColor={iconColor} />;
  }

  return (
    icon &&
    (onClick
      ? withTooltip(
          label || (w ? w.prompt : flow),
          <IconButton
            id="open-workflows-btn"
            disabled={disabled}
            style={{ opacity: disabled ? 0.3 : 1 }}
            onClick={onClick}
          >
            {icon}
          </IconButton>
        )
      : withTooltip(label || w?.prompt, icon))
  );
}
